import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { TextField, Box } from '@mui/material';

const DigitInput = ({
  value,
  onChange,
  onPasteAll,
  index,
  hasError,
  onComplete,
  disabled,
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (index === 0 && !disabled) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [index, disabled]);

  const handleChange = (event) => {
    const { value: inputValue } = event.target;
    if (!/^\d*$/.test(inputValue)) return;
    if (inputValue.length > 1) return;
    onChange(index, inputValue);
    if (inputValue && index < 5) {
      const nextInput = document.querySelector(`input[data-index="${index + 1}"]`);
      if (nextInput) nextInput.focus();
    }
    if (inputValue && index === 5) onComplete();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Backspace' && !value && index > 0) {
      const prevInput = document.querySelector(`input[data-index="${index - 1}"]`);
      if (prevInput) prevInput.focus();
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      const prevInput = document.querySelector(`input[data-index="${index - 1}"]`);
      if (prevInput) prevInput.focus();
    }
    if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      const nextInput = document.querySelector(`input[data-index="${index + 1}"]`);
      if (nextInput) nextInput.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text').replace(/\D/g, '');
    if (/^\d{6}$/.test(pastedData)) {
      onPasteAll(pastedData.split(''));
      const lastInput = document.querySelector('input[data-index="5"]');
      if (lastInput) lastInput.focus();
    }
  };

  return (
    <TextField
      ref={inputRef}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      inputProps={{
        'data-index': index,
        maxLength: 1,
        style: {
          textAlign: 'center',
          fontSize: '20px',
          fontWeight: 'bold',
          padding: '12px',
        },
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          '& fieldset': {
            borderColor: hasError ? '#EA2027' : '#e1e5e9',
            borderWidth: '2px',
          },
          '&:hover fieldset': {
            borderColor: hasError ? '#EA2027' : '#2196f3',
          },
          '&.Mui-focused fieldset': {
            borderColor: hasError ? '#EA2027' : '#2196f3',
          },
        },
        width: '50px',
        height: '50px',
      }}
      disabled={disabled}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
    />
  );
};

DigitInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onPasteAll: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  hasError: PropTypes.bool,
  onComplete: PropTypes.func,
  disabled: PropTypes.bool,
};

DigitInput.defaultProps = {
  hasError: false,
  onComplete: () => {},
  disabled: false,
};

const DigitInputGroup = ({
  values,
  onChange,
  onPasteAll,
  hasError,
  onComplete,
  disabled,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(6, 1fr)',
      gap: '12px',
      maxWidth: '400px',
      margin: '20px auto',
    }}
  >
    {Array.from({ length: 6 }, (_, index) => (
      <DigitInput
        key={index}
        value={values[index] || ''}
        onChange={onChange}
        onPasteAll={onPasteAll}
        index={index}
        hasError={hasError}
        onComplete={onComplete}
        disabled={disabled}
      />
    ))}
  </Box>
);

DigitInputGroup.propTypes = {
  values: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  onPasteAll: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
  onComplete: PropTypes.func,
  disabled: PropTypes.bool,
};

DigitInputGroup.defaultProps = {
  hasError: false,
  onComplete: () => {},
  disabled: false,
};

export default DigitInputGroup;
